import { useCallback, useMemo, useRef } from "react";
import { GoogleMap, Marker } from "@react-google-maps/api";
import { Box, Text, BlockStack, Banner, Button } from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import { LocationIcon } from "@medentic/polaris-icons";

// Google Maps API key from environment variable
const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

interface MapPickerProps {
  latitude: number;
  longitude: number;
  onCoordinatesChange: (lat: number, lng: number) => void;
}

const mapContainerStyle = {
  width: "100%",
  height: "400px",
};

const mapOptions: google.maps.MapOptions = {
  disableDefaultUI: false,
  zoomControl: true,
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: false,
  gestureHandling: "greedy",
};

export default function MapPicker({
  latitude,
  longitude,
  onCoordinatesChange,
}: MapPickerProps) {
  const { t } = useTranslation();
  const mapRef = useRef<google.maps.Map | null>(null);
  const markerPosition = useMemo(
    () => ({ lat: latitude, lng: longitude }),
    [latitude, longitude]
  );

  // Handle map load
  const handleMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);

  // Handle marker drag end - update parent
  const handleMarkerDragEnd = useCallback(
    (e: google.maps.MapMouseEvent) => {
      if (e.latLng) {
        const newPosition = {
          lat: e.latLng.lat(),
          lng: e.latLng.lng(),
        };
        onCoordinatesChange(newPosition.lat, newPosition.lng);
      }
    },
    [onCoordinatesChange]
  );

  // Recenter map to marker position
  const handleRecenter = useCallback(() => {
    if (mapRef.current) {
      mapRef.current.panTo(markerPosition);
      mapRef.current.setZoom(15);
    }
  }, [markerPosition]);

  // Show error if API key is not configured
  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <Banner tone="warning">
        <p>
          {t('events.component.address_picker.api_key_missing', 'Google Maps API key is not configured. Please add VITE_GOOGLE_MAPS_API_KEY to your .env file.')}
        </p>
      </Banner>
    );
  }

  return (
    <BlockStack gap="200">
      <Box
        borderColor="border"
        borderWidth="025"
        borderRadius="200"
        position="relative"
      >
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={markerPosition}
          zoom={15}
          options={mapOptions}
          onLoad={handleMapLoad}
        >
          <Marker
            position={markerPosition}
            draggable={true}
            onDragEnd={handleMarkerDragEnd}
          />
        </GoogleMap>
        
        {/* Floating recenter button inside the map */}
        <div
          style={{
            position: 'absolute',
            bottom: '20px',
            right: '10px',
            zIndex: 1,
          }}
        >
          <Button
            size="medium"
            onClick={handleRecenter}
            icon={LocationIcon}
            accessibilityLabel={t('events.component.address_picker.recenter_map', 'Find marker')}
          />
        </div>
      </Box>

      <Text as="p" variant="bodySm" tone="subdued">
        {t('events.component.address_picker.drag_pin_instruction', 'Drag the pin to adjust the exact location')}
      </Text>
    </BlockStack>
  );
}
